'use client';

import React, { useContext, useEffect, useState, useRef, useCallback } from 'react';
import { usePathname } from 'next/navigation';
import { AdminSidebarContext } from '@/contexts/AdminSidebarContext';

interface PageContentWrapperProps {
  children: React.ReactNode;
}

/**
 * Client component wrapper that adapts page content to admin sidebar state
 */
export default function PageContentWrapper({ children }: PageContentWrapperProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  
  // Check if we're in preview mode by looking for preview container in parent tree
  useEffect(() => {
    if (wrapperRef.current) {
      const previewContainer = wrapperRef.current.closest('[data-preview-mode="true"]');
      setIsPreviewMode(!!previewContainer);
    }
  }, []);
  
  // Also check on mount synchronously using a callback ref
  const checkPreviewMode = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      const previewContainer = node.closest('[data-preview-mode="true"]');
      if (previewContainer) {
        setIsPreviewMode(true);
      }
    }
  }, []);
  
  // Check if we're in an admin route and get sidebar state
  const pathname = usePathname();
  const isAdminRoute = pathname?.startsWith('/admin') || pathname?.startsWith('/admin/dashboard/pages');
  const adminSidebarContext = useContext(AdminSidebarContext);
  const isAdminSidebarCollapsed = adminSidebarContext?.isCollapsed ?? false;
  
  // State for responsive behavior
  const [isMobile, setIsMobile] = useState(false);
  
  // Update mobile state on mount and resize
  // In preview mode, always use desktop viewport (not mobile)
  useEffect(() => {
    if (isPreviewMode) {
      setIsMobile(false);
      return;
    }
    
    const updateMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    updateMobile();
    window.addEventListener('resize', updateMobile);
    return () => window.removeEventListener('resize', updateMobile);
  }, [isPreviewMode]);
  
  // Calculate styles for admin routes
  const getContentStyles = (): React.CSSProperties & { [key: string]: any } => {
    const baseStyles: React.CSSProperties & { [key: string]: any } = {
      // Removed overscrollBehavior restrictions to allow normal scrolling
      marginLeft: 0, // Explicitly set to 0, will be overridden if not in preview mode
    };
    
    // Don't apply sidebar margin in preview mode
    if (isPreviewMode) {
      return baseStyles;
    }
    
    if (!isAdminRoute || !adminSidebarContext) {
      return baseStyles;
    }
    
    // On mobile (< 1024px), sidebar overlays, so content should be full width
    if (isMobile) {
      return baseStyles;
    }
    
    // On desktop (>= 1024px), account for sidebar width
    return {
      ...baseStyles,
      marginLeft: isAdminSidebarCollapsed ? '4rem' : '14rem', // 64px collapsed, 224px expanded
      width: isAdminSidebarCollapsed 
        ? 'calc(100% - 4rem)' // 100% - 64px (collapsed sidebar)
        : 'calc(100% - 14rem)', // 100% - 224px (expanded sidebar)
    };
  };

  // Combine refs to check preview mode on mount
  const combinedRef = useCallback((node: HTMLDivElement | null) => {
    wrapperRef.current = node;
    checkPreviewMode(node);
  }, [checkPreviewMode]);

  return (
    <div 
      ref={combinedRef}
      className="min-h-screen flex flex-col overflow-x-hidden w-full relative transition-all duration-300"
      style={getContentStyles()}
    >
      {children}
    </div>
  );
}














